import React, { Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Index from "./pages/Index";

const Publications = React.lazy(() => import("./pages/Publications"));
const NorthPop = React.lazy(() => import("./pages/NorthPop"));
const Neurodevelopment = React.lazy(() => import("./pages/Neurodevelopment"));
const Neonatology = React.lazy(() => import("./pages/Neonatology"));
const Nutrition = React.lazy(() => import("./pages/Nutrition"));
const Impact = React.lazy(() => import("./pages/Impact"));
const About = React.lazy(() => import("./pages/About"));
const News = React.lazy(() => import("./pages/News"));
const NewsDetail = React.lazy(() => import("./pages/NewsDetail"));
const AdminLogin = React.lazy(() => import("./pages/admin/Login"));
const AdminDashboard = React.lazy(() => import("./pages/admin/Dashboard"));
const NewsEditor = React.lazy(() => import("./pages/admin/NewsEditor"));
const ProjectsDashboard = React.lazy(() => import("./pages/admin/ProjectsDashboard"));
const ProjectEditor = React.lazy(() => import("./pages/admin/ProjectEditor"));
const PublicationsDashboard = React.lazy(() => import("./pages/admin/PublicationsDashboard"));
const PublicationEditor = React.lazy(() => import("./pages/admin/PublicationEditor"));
const TeamsDashboard = React.lazy(() => import("./pages/admin/TeamsDashboard"));
const TeamMemberEditor = React.lazy(() => import("./pages/admin/TeamMemberEditor"));
const IronResearch = React.lazy(() => import("./pages/IronResearch"));
const BreastMilkResearch = React.lazy(() => import("./pages/BreastMilkResearch"));
const Preterm = React.lazy(() => import("./pages/Preterm"));
const ChangePassword = React.lazy(() => import("./pages/admin/ChangePassword"));
const PagesDashboard = React.lazy(() => import("./pages/admin/PagesDashboard"));
const PageContentEditor = React.lazy(() => import("./pages/admin/PageContentEditor"));
const NotFound = React.lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<div className="flex h-screen items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" /></div>}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/nutrition" element={<Nutrition />} />
            <Route path="/impact" element={<Impact />} />
            <Route path="/neurodevelopment" element={<Neurodevelopment />} />
            <Route path="/neonatology" element={<Neonatology />} />
            <Route path="/northpop" element={<NorthPop />} />
            <Route path="/about" element={<About />} />
            <Route path="/publications" element={<Publications />} />
            <Route path="/iron-research" element={<IronResearch />} />
            <Route path="/n4-breast-milk-and-infant-formula-research" element={<BreastMilkResearch />} />
            <Route path="/preterm" element={<Preterm />} />
            <Route path="/news" element={<News />} />
            <Route path="/news/:slug" element={<NewsDetail />} />
            
            {/* Admin Routes - Hidden from crawlers */}
            <Route path="/n4-manage/login" element={<AdminLogin />} />
            <Route path="/n4-manage/change-password" element={<ChangePassword />} />
            <Route path="/n4-manage" element={<AdminDashboard />} />
            <Route path="/n4-manage/new" element={<NewsEditor />} />
            <Route path="/n4-manage/edit/:id" element={<NewsEditor />} />
            <Route path="/n4-manage/projects" element={<ProjectsDashboard />} />
            <Route path="/n4-manage/projects/new" element={<ProjectEditor />} />
            <Route path="/n4-manage/projects/edit/:id" element={<ProjectEditor />} />
            <Route path="/n4-manage/publications" element={<PublicationsDashboard />} />
            <Route path="/n4-manage/publications/new" element={<PublicationEditor />} />
            <Route path="/n4-manage/publications/edit/:id" element={<PublicationEditor />} />
            <Route path="/n4-manage/teams" element={<TeamsDashboard />} />
            <Route path="/n4-manage/teams/new" element={<TeamMemberEditor />} />
            <Route path="/n4-manage/teams/edit/:id" element={<TeamMemberEditor />} />
            <Route path="/n4-manage/pages" element={<PagesDashboard />} />
            <Route path="/n4-manage/pages/edit/:pageSlug" element={<PageContentEditor />} />
            
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
